// ====================================
// MAIN API FUNCTION FOR HOSTING SUBMISSION
// ====================================

import { apiSend, getRoute } from "@services/axios";
import { store } from "@services/redux/store";

/**
 * Main function to handle hosting product submissions by step level
 * @param {string|null} productId - Product ID (null for step 1, required for steps 2-3)
 * @param {Object} productData - Product data for the current step
 * @param {number} level - Step level (1, 2, or 3)
 * @returns {Promise} API response
 */
export const postHosting = async (productId = null, productData, level) => {
  switch (level) {
    case 1:
      return await postHostingStep1(productData);

    case 2:
      return await postHostingStep2(productId);

    case 3:
      return await postHostingStep3(productId);

    default:
      throw new Error(`Unsupported level: ${level} for hosting product`);
  }
};

export default postHosting;

// ====================================
// STEP 1 - CREATE HOSTING PRODUCT
// ====================================

export const postHostingStep1 = async (productData) => {
  try {
    const url = `${getRoute("create_hosting_step_1")}`;
    const response = await apiSend(url, "post", productData);
    console.log(response.data);

    handleBackendErrors(response.data);
    localStorage.setItem("product_id_save", response?.data?.result?.product_id);
    return response.data.result;
  } catch (error) {
    throw formatBackendError(error);
  }
};

// ====================================
// STEP 2 - UPDATE HOSTING DETAILS
// ====================================

export const postHostingStep2 = async (productId) => {
  try {
    let productData = store.getState().hosting?.step2;

    const url = `${getRoute("create_hosting_step_2")}/${productId}`;
    const response = await apiSend(url, "post", productData);
    console.log(response.data);
    handleBackendErrors(response.data);
    return response.data.result;
  } catch (error) {
    throw formatBackendError(error);
  }
};

// ====================================
// STEP 3 - FINAL PRICING + POLICIES
// ====================================

export const postHostingStep3 = async (productId) => {
  try {
    let productData = store.getState().hosting?.step3;

    const url = `${getRoute("create_hosting_step_3")}/${productId}`;
    const response = await apiSend(url, "post", productData);
    console.log(response.data);
    handleBackendErrors(response.data);
    return response.data.result;
  } catch (error) {
    throw formatBackendError(error);
  }
};

// ====================================
// HELPER FUNCTIONS
// ====================================

/**
 * Detects and throws an error if the backend response indicates a problem.
 * If "invalid request data" is found, logs the backend error table.
 */
function handleBackendErrors(data) {
  if (!data) return;

  const backendMsg = data?.request?.message;
  const backendError = data?.result?.Error;
  const backendStatus = data?.request?.status;

  // Case 1: Check for status=false
  if (backendStatus === false) {
    throw new Error(backendMsg || "Backend request failed");
  }

  // Case 2: Invalid request data — print the backend error table
  if (backendMsg && backendMsg.toLowerCase().includes("invalid request data")) {
    if (backendError && typeof backendError === "object") {
      console.table(flattenBackendErrors(backendError));
    }
    throw new Error(backendMsg);
  }

  // Case 3: Generic backend Error object
  if (backendError && typeof backendError === "object") {
    const firstKey = Object.keys(backendError)[0];
    const firstMsg = backendError[firstKey]?.[0] || "Unknown backend error";
    throw new Error(firstMsg);
  }
}

/**
 * Formats caught backend/network errors for consistent error throwing.
 */
function formatBackendError(error) {
  if (error?.response?.data) {
    handleBackendErrors(error.response.data);
  }
  return new Error(
    error.message || "An unknown error occurred during hosting submission."
  );
}

/**
 * Converts nested backend error objects to a flat list suitable for console.table.
 * Example:
 * {
 *   "equipment.0": ["Field is required"]
 * }
 * → [{ field: "equipment.0", message: "Field is required" }]
 */
function flattenBackendErrors(errorObj) {
  const rows = [];
  for (const key in errorObj) {
    const value = errorObj[key];
    if (Array.isArray(value)) {
      value.forEach((msg) => rows.push({ field: key, message: msg }));
    } else if (typeof value === "object") {
      for (const subKey in value) {
        rows.push({ field: `${key}.${subKey}`, message: value[subKey] });
      }
    } else {
      rows.push({ field: key, message: value });
    }
  }
  return rows;
}
